import { useMemo, useCallback, useEffect, useRef } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  type Node,
  type Edge,
  Handle,
  Position,
  type ReactFlowInstance,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { stratify, tree } from 'd3-hierarchy';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { useSeats } from '../../api/seats';
import type { Seat, SeatHolder } from '../../types';

// =============================================================================
// Node
// =============================================================================

interface SeatNodeData {
  seat: Seat;
  [key: string]: unknown;
}

function holderName(h: SeatHolder): string {
  return [h.firstname, h.lastname].filter(Boolean).join(' ') || h.email || 'Unknown';
}

function SeatNode({ data }: { data: SeatNodeData }) {
  const navigate = useNavigate();
  const { seat } = data;
  const primary = seat.holders.find((h) => h.is_primary);
  const extras = seat.holders.filter((h) => !h.is_primary);
  const vacant = seat.holders.length === 0;

  const teamTone: Record<string, string> = {
    Leadership: 'bg-purple-100 text-purple-700',
    Sales: 'bg-blue-100 text-blue-700',
    Operations: 'bg-orange-100 text-orange-700',
    RnD: 'bg-green-100 text-green-700',
    Finances: 'bg-yellow-100 text-yellow-700',
  };

  return (
    <div
      onClick={() => navigate(`/os/accountability/seats/${seat.id}`)}
      className={`
        cursor-pointer group
        bg-white border rounded-xl shadow-sm
        px-3 py-2.5
        min-w-[220px] max-w-[260px]
        hover:shadow-md hover:border-primary/40 transition-all
        ${vacant ? 'border-dashed border-zinc-300' : 'border-border'}
      `}
    >
      <Handle type="target" position={Position.Top} className="!bg-border !w-2 !h-2" />

      {/* Title + team badges */}
      <div className="flex items-start justify-between gap-1 mb-2">
        <div className="text-sm font-semibold truncate">{seat.title}</div>
      </div>

      {/* Teams */}
      {seat.teams.length > 0 && (
        <div className="flex flex-wrap gap-0.5 mb-2">
          {seat.teams.map((t) => (
            <span
              key={t.id}
              className={`text-[9px] font-medium px-1.5 py-0.5 rounded ${teamTone[t.name] ?? 'bg-zinc-100 text-zinc-700'}`}
            >
              {t.name}
            </span>
          ))}
        </div>
      )}

      {/* Holder(s) */}
      {vacant ? (
        <div className="text-[11px] italic text-muted-foreground py-1">Vacant</div>
      ) : (
        <div className="space-y-1">
          {primary && (
            <div className="flex items-center gap-2">
              {primary.profile_photo_url ? (
                <img
                  src={primary.profile_photo_url}
                  alt=""
                  className="h-7 w-7 rounded-full object-cover ring-1 ring-border shrink-0"
                />
              ) : (
                <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-semibold text-primary shrink-0">
                  {holderName(primary).split(' ').map((p) => p[0]).join('').toUpperCase().slice(0, 2)}
                </div>
              )}
              <div className="min-w-0">
                <div className="text-xs font-medium truncate">{holderName(primary)}</div>
                {primary.territory && (
                  <div className="text-[10px] text-muted-foreground truncate">{primary.territory}</div>
                )}
              </div>
            </div>
          )}
          {extras.length > 0 && (
            <div className="flex items-center gap-1 pt-1 border-t border-border">
              <div className="flex -space-x-1">
                {extras.slice(0, 4).map((h) => (
                  <div key={h.id} title={holderName(h)} className="shrink-0">
                    {h.profile_photo_url ? (
                      <img
                        src={h.profile_photo_url}
                        alt=""
                        className="h-5 w-5 rounded-full object-cover ring-1 ring-white"
                      />
                    ) : (
                      <div className="h-5 w-5 rounded-full bg-primary/10 ring-1 ring-white flex items-center justify-center text-[8px] font-semibold text-primary">
                        {holderName(h).split(' ').map((p) => p[0]).join('').toUpperCase().slice(0, 2)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {extras.length > 0 && (
                <span className="text-[10px] text-muted-foreground">+{extras.length} more</span>
              )}
            </div>
          )}
        </div>
      )}

      <Handle type="source" position={Position.Bottom} className="!bg-border !w-2 !h-2" />
    </div>
  );
}

const nodeTypes = { seatNode: SeatNode };

// =============================================================================
// Tree layout using d3-hierarchy
//
// Produces a clean top-down tree with evenly distributed siblings. Curved
// smoothstep edges give the "springy" feel while keeping the hierarchy readable.
// =============================================================================

const NODE_W = 260;
const NODE_H = 130;
const H_SPACING = NODE_W + 30;
const V_SPACING = NODE_H + 60;

function runLayout(seats: Seat[]): { nodes: Node[]; edges: Edge[] } {
  if (!seats.length) return { nodes: [], edges: [] };

  const seatById = new Map(seats.map((s) => [s.id, s]));

  // Stratify needs a single root. If multiple top-level seats exist, inject a
  // synthetic root and connect all orphans to it; we filter it out of the output.
  const SYNTHETIC_ROOT = '__root__';
  const flat = seats.map((s) => ({
    id: s.id,
    parentId: s.parent_seat_id && seatById.has(s.parent_seat_id) ? s.parent_seat_id : SYNTHETIC_ROOT,
    seat: s,
  }));
  flat.push({ id: SYNTHETIC_ROOT, parentId: null as unknown as string, seat: null as unknown as Seat });

  const root = stratify<{ id: string; parentId: string | null; seat: Seat }>()
    .id((d) => d.id)
    .parentId((d) => d.parentId)(flat);

  tree<{ id: string; parentId: string | null; seat: Seat }>()
    .nodeSize([H_SPACING, V_SPACING])(root);

  const rfNodes: Node[] = [];
  const rfEdges: Edge[] = [];

  root.each((n) => {
    if (n.data.id === SYNTHETIC_ROOT) return;
    rfNodes.push({
      id: n.data.id,
      type: 'seatNode',
      position: { x: (n.x ?? 0) - NODE_W / 2, y: (n.y ?? 0) },
      data: { seat: n.data.seat } as SeatNodeData,
    });
    if (n.parent && n.parent.data.id !== SYNTHETIC_ROOT) {
      rfEdges.push({
        id: `${n.parent.data.id}-${n.data.id}`,
        source: n.parent.data.id,
        target: n.data.id,
        type: 'smoothstep',
        style: { stroke: '#cbd5e1', strokeWidth: 2 },
      });
    }
  });

  return { nodes: rfNodes, edges: rfEdges };
}

// =============================================================================
// Tab
// =============================================================================

export function OrgChartTab() {
  const { data, isLoading } = useSeats();
  const seats = data?.seats ?? [];

  const { nodes: initialNodes, edges: initialEdges } = useMemo(
    () => runLayout(seats),
    [seats]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Re-apply layout whenever the seat list changes (create/delete/reparent)
  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  const instanceRef = useRef<ReactFlowInstance | null>(null);
  const onInit = useCallback((instance: ReactFlowInstance) => {
    instanceRef.current = instance;
    instance.fitView({ padding: 0.2 });
  }, []);

  if (isLoading) {
    return (
      <div className="grid grid-cols-3 gap-4 p-6">
        {Array.from({ length: 9 }).map((_, i) => (
          <Skeleton key={i} className="h-20 rounded-xl" />
        ))}
      </div>
    );
  }

  if (!seats.length) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">
        No seats defined. Create seats in the Seats tab first.
      </div>
    );
  }

  return (
    <div className="h-[720px] w-full rounded-xl border bg-muted/20 overflow-hidden">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        onInit={onInit}
        fitView
        fitViewOptions={{ padding: 0.2 }}
        minZoom={0.3}
        maxZoom={1.5}
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={20} size={1} color="#e2e8f0" />
        <Controls showInteractive={false} />
        <MiniMap
          nodeColor={() => '#cbd5e1'}
          maskColor="rgba(255,255,255,0.7)"
          className="!border-border"
        />
      </ReactFlow>
    </div>
  );
}
